/**
 * Turn the page: back one, forward one, or type the page to go to.
 *
 * The number is a field rather than a menu because a reader who wants page 23 of 30 knows
 * the number, and typing it is faster than finding it in a list. What is typed is taken on
 * Enter or when the field is left, not on every keystroke -- "2" on the way to "23" is not
 * a request to see page 2.
 *
 * It sits in the transport of whichever desk it turns, so it comes in that desk's two looks:
 * the dark strip of the Arrange panels and the paper status bar of the Editor.
 */

import { useEffect, useState } from "react";

export interface ScorePagerProps {
  /** 1-based. */
  readonly page: number;
  readonly count: number;
  onPage(page: number): void;
  readonly look: "shell" | "paper";
}

const LOOKS = {
  shell: {
    button:
      "h-7 w-7 rounded text-[13px] leading-none text-shell-text hover:bg-shell-hot disabled:opacity-40",
    field:
      "h-7 w-10 rounded bg-shell-key px-1 text-center font-mono text-[11px] text-shell-lit outline-none hover:bg-shell-hot",
    label: "font-mono text-[11px] text-shell-dim",
  },
  paper: {
    button:
      "h-8 w-8 rounded-[8px] border border-rule text-[12px] text-ink-faded hover:border-ink-quiet hover:text-ink disabled:opacity-40",
    field:
      "h-8 w-11 rounded-[8px] border border-rule px-1 text-center font-mono text-[11px] text-ink outline-none focus:border-ink-quiet",
    label: "font-mono text-[11px] text-ink-faded",
  },
} as const;

export default function ScorePager({
  page,
  count,
  onPage,
  look,
}: ScorePagerProps): JSX.Element | null {
  const [draft, setDraft] = useState(String(page));
  useEffect(() => setDraft(String(page)), [page]);

  // One sheet has nowhere to turn to.
  if (count < 2) return null;

  const classes = LOOKS[look];
  const commit = (): void => {
    const asked = Number(draft);
    if (draft.trim() === "" || !Number.isFinite(asked)) {
      setDraft(String(page));
      return;
    }
    // Shown as turned to even where it is the page already, so "99" does not stay in the box.
    const to = Math.min(Math.max(Math.round(asked), 1), count);
    setDraft(String(to));
    onPage(to);
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        className={classes.button}
      >
        ◀
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
          if (event.key === "Escape") {
            // Said, so a desk filling the window stays filled when only the typing is undone.
            event.preventDefault();
            setDraft(String(page));
          }
        }}
        aria-label="Page"
        className={classes.field}
      />
      <span className={classes.label}>of {count}</span>
      <button
        type="button"
        onClick={() => onPage(page + 1)}
        disabled={page >= count}
        aria-label="Next page"
        className={classes.button}
      >
        ▶
      </button>
    </div>
  );
}
